import { motion } from "framer-motion";
import { Zap, Compass, AlertTriangle, Heart, FileText, Shield } from "lucide-react";

export function SolutionSection() {
  const differentiators = [
    {
      icon: Zap,
      title: "Dynamic Logic",
      description: "Adapts to your unique situation, beyond a standard checklist.",
      color: "#14b8a6"
    },
    {
      icon: Compass,
      title: "Clear Next Steps",
      description: "Provides clear guidance following a thorough assessment.",
      color: "#10B981"
    },
    {
      icon: AlertTriangle,
      title: "Automatic Alerts",
      description: "Triggers alerts for emergencies (like '000' or hospital warnings) when urgent help is required.",
      color: "#F59E0B"
    },
    {
      icon: Heart,
      title: "Empathetic Support",
      description: "Offers compassionate guidance and understanding throughout your journey.",
      color: "#EF4444"
    },
    {
      icon: FileText,
      title: "Comprehensive Summary",
      description: "Generates detailed reports for a smooth handover to your healthcare professionals.",
      color: "#8B5CF6"
    },
    {
      icon: Shield,
      title: "Evidence-Based Care",
      description: "Built on proven clinical research and validated methodologies for Australian healthcare.",
      color: "#06B6D4"
    }
  ];

  return (
    <section id="solution" className="py-20 relative z-10 bg-white dark:bg-black">
      <div className="container mx-auto px-6">
        <motion.div 
          className="text-center mb-16 text-black dark:text-white"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-black dark:text-white text-3xl md:text-4xl font-bold mb-6">
            Transforming the Patient Experience with <span className="text-teal-600 dark:text-teal-400">Intelligent & Caring Technology</span>
          </h2>
          <p className="text-xl text-black dark:text-white max-w-4xl mx-auto mb-8">
            Elevita AI is more than a symptom checker; it's a comprehensive triage assistant built from real-life experience, ensuring no one is overlooked, dismissed, or left struggling whilst waiting.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16 bg-white dark:bg-black">
          {differentiators.map((item, index) => (
            <motion.div
              key={item.title}
              className="bg-white/80 dark:bg-black/80 p-8 rounded-xl border border-gray-600 hover:border-[#14b8a6] transition-all duration-300 text-center group"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
            >
              <div 
                className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110 bg-white dark:bg-black"
                style={{ backgroundColor: `${item.color}20` }}
              >
                <item.icon size={32} style={{ color: item.color }} />
              </div>
              <h3 className="text-black dark:text-white text-xl font-bold mb-4" style={{ color: item.color }}>
                {item.title}
              </h3>
              <p className="text-black dark:text-white leading-relaxed">{item.description}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="text-center mt-16 text-black dark:text-white"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <div className="max-w-4xl mx-auto text-black dark:text-white">
            <h3 className="text-2xl font-bold mb-8 text-[#14b8a6]">How Elevita Works</h3>
            <svg viewBox="0 0 800 400" className="w-full h-64 mb-6">
              <motion.g
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: 0.8 }}
              >
                {/* Central Elevita hub with pulsing effect */}
                <motion.circle 
                  cx="400" cy="200" r="50" 
                  fill="#14b8a6" 
                  opacity="0.2"
                  animate={{ 
                    scale: [1, 1.1, 1],
                    opacity: [0.2, 0.3, 0.2]
                  }}
                  transition={{ 
                    duration: 3,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                />
                <circle cx="400" cy="200" r="40" fill="#14b8a6" opacity="0.4" />
                <text x="400" y="205" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">
                  Elevita AI
                </text>
                
                {/* Connected features in a hexagonal arrangement */}
                {differentiators.map((item, index) => {
                  const angle = (index * 60) - 90; // 60 degrees apart for 6 items
                  const x = 400 + 150 * Math.cos(angle * Math.PI / 180);
                  const y = 200 + 150 * Math.sin(angle * Math.PI / 180);
                  
                  return (
                    <motion.g key={item.title}>
                      {/* Feature circle with icon simulation */}
                      <motion.circle 
                        cx={x} cy={y} r="30" 
                        fill={item.color} 
                        opacity="0.3"
                        whileHover={{ opacity: 0.6, scale: 1.1 }}
                      />
                      <circle cx={x} cy={y} r="25" fill={item.color} opacity="0.6" />
                      
                      {/* Animated connection lines */}
                      <motion.line
                        x1="400" y1="200" x2={x} y2={y}
                        stroke={item.color}
                        strokeWidth="2"
                        strokeDasharray="5,5"
                        opacity="0.4"
                        initial={{ pathLength: 0 }}
                        whileInView={{ pathLength: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.5, delay: 0.8 + index * 0.1 }}
                      />
                      
                      {/* Data flow animation */}
                      <motion.circle
                        cx="400" cy="200" r="3"
                        fill={item.color}
                        initial={{ cx: 400, cy: 200 }}
                        animate={{ cx: x, cy: y }}
                        transition={{
                          duration: 2,
                          delay: 2 + index * 0.3,
                          repeat: Infinity,
                          repeatDelay: 3
                        }}
                      />
                    </motion.g>
                  );
                })}
                
                {/* Patient journey flow indicators */}
                <motion.path
                  d="M 250,200 Q 400,100 550,200"
                  stroke="#14b8a6"
                  strokeWidth="1"
                  fill="none"
                  opacity="0.3"
                  strokeDasharray="10,5"
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 2, delay: 1.5 }}
                />
                
                {/* Labels for start and end of journey */}
                <text x="250" y="220" fill="#B0B0B0" fontSize="12">Patient Input</text>
                <text x="550" y="220" fill="#B0B0B0" fontSize="12">Care Delivered</text>
              </motion.g>
            </svg>
            <p className="text-[#B0B0B0] italic mb-8">
              "Intelligent orchestration of healthcare technology working seamlessly for Australian patients"
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}